import React, { Context, ReactNode, useCallback, useState } from 'react';
import { useQuery } from '@apollo/client';

import {
    constants,
    getBuildEnvironment,
    getWebpackEnvVar,
    getInLocalStorage,
    setInLocalStorage,
} from '@trqk/utils';
import { queryStrings } from '@trqk/resolvers';
import { LOG_POLLING_INTERVAL_SHORT } from '@trqk/utils/src/constants';
import { useInterval } from '..';

import { handleLogDetails, getLogEvents } from './utils';

const { GET_SESSION_CURRENT_USER, GET_ACTIVE_LOG_POLLS } = queryStrings;
const { LOG_POLLING_INTERVAL_LONG, LOG_FETCHING_LOOKBACK } = constants;

const bucketName = getWebpackEnvVar('web', 'AWS_S3_LOGS_BUCKET');
const bucketRegion = getWebpackEnvVar('web', 'AWS_REGION');
const identityPoolId = getWebpackEnvVar('web', 'AWS_IDENTITY_POOL_ID');

interface LogDataProviderProps {
    children: ReactNode;
    LogDataContext: Context<LogDataProviderValue>;
    scriptBlockerFlagsLocalStorageKey: string;
}

const getStartAfterLocalStorageKey = (userId: string): string =>
    `logsStartAfter.user:${userId}`;

const SCRIPT_BLOCKER_CALL_SITE_ID = 'LogDataProvider';

const buildEnvironment = getBuildEnvironment();

/**
 * Sets up polling for log events for the current user from the S3 bucket that contains logs for the
 * current environment. Returns the entire list of LogEvents to the caller of the hook and updates
 * it each time polling completes. Also manages the handler functions for each event type, and
 * downloads the detailed JSON data for event types that have a handler. This is governed by some
 * detailed timing logic:
 * - the log polling interval is read from the Apollo Client cache so it can be set externally
 * - S3 requests to download the JSON data are throttled so as to avoid a TooManyRequests error
 * related to Cognito requests fired internally by the AWS S3 SDK.
 * @param props - {@link LogDataProviderProps}
 */
export const LogDataProvider = ({
    children,
    LogDataContext,
    scriptBlockerFlagsLocalStorageKey,
}: LogDataProviderProps): JSX.Element => {
    const currentUserQuery = useQuery(GET_SESSION_CURRENT_USER);
    const activeLogPollsQuery = useQuery(GET_ACTIVE_LOG_POLLS);
    const activeLogPolls =
        activeLogPollsQuery?.data?.uiState?.activeLogPolls ?? null;
    const hasActivePolls =
        activeLogPolls instanceof Object &&
        Object.values(activeLogPolls).some(
            (status: unknown): boolean => status === true,
        );
    const pollingInterval = hasActivePolls
        ? LOG_POLLING_INTERVAL_SHORT
        : LOG_POLLING_INTERVAL_LONG;
    const userId = currentUserQuery?.data?.session?.currentUser?.id;

    const [logs, setLogs] = useState<LogEvent[]>([]);

    // handlers is a nested object like:
    //      {
    //          dashboardBuilt: {   // outer key is event type
    //              uploadStatements: myHandler         // each inner "handlerKey" isolates this
    //              uploadAudioFiles: myOtherHandler    // handler from others that listen for the
    //                                                  // same event
    //          },
    //      }
    const [handlers, setHandlers] = useState<HandlersState>({});

    // We will use useInterval to trigger the data fetching process, but we don't want to start the
    // process if the previous one has not completed yet, so we use this flag to check for completion.
    const [loading, setLoading] = useState(false);

    const [retryQueue, setRetryQueue] = useState<LogEvent[]>([]);

    const fetchLogEvents = useCallback((): void => {
        if (userId && !loading) {
            setLoading(true);

            const startAfter = getInLocalStorage<string>(
                getStartAfterLocalStorageKey(userId),
            );

            const getAndHandleLogEvents = async (): Promise<void> => {
                // Before fetching log events, set a flag for this call site in localStorage for
                // CheckForScriptBlocker. See: packages/web/src/components/modals/CheckForScriptBlocker/CheckForScriptBlocker.tsx
                setInLocalStorage<Record<string, number>>(
                    scriptBlockerFlagsLocalStorageKey,
                    (
                        prev: Record<string, number> | null,
                    ): Record<string, number> => {
                        const next =
                            prev === null
                                ? {}
                                : {
                                      ...prev,
                                      [SCRIPT_BLOCKER_CALL_SITE_ID]: Date.now(),
                                  };

                        return next;
                    },
                );

                try {
                    const newLogEvents = await getLogEvents({
                        bucketName,
                        bucketRegion,
                        identityPoolId,
                        lookback: LOG_FETCHING_LOOKBACK,
                        startAfter,
                        userId,
                    });

                    // After successfully fetching log events, delete the flag for this call site in
                    // localStorage for CheckForScriptBlocker. See: packages/web/src/components/modals/CheckForScriptBlocker/CheckForScriptBlocker.tsx
                    setInLocalStorage<Record<string, number>>(
                        scriptBlockerFlagsLocalStorageKey,
                        (
                            prev: Record<string, number> | null,
                        ): Record<string, number> => {
                            const next = prev === null ? {} : { ...prev };

                            delete next[SCRIPT_BLOCKER_CALL_SITE_ID];

                            return next;
                        },
                    );

                    if (newLogEvents.length > 0 && userId) {
                        const lastEvent = newLogEvents[newLogEvents.length - 1];

                        setInLocalStorage(
                            getStartAfterLocalStorageKey(userId),
                            (): string => lastEvent.key,
                        );

                        const { eventsToRetry, logEventsWithDetails } =
                            await handleLogDetails({
                                logEvents: [...retryQueue, ...newLogEvents],
                                handlers,
                            });

                        setLogs((prev: LogEvent[]): LogEvent[] => [
                            ...prev,
                            ...logEventsWithDetails,
                        ]);

                        setRetryQueue(eventsToRetry);
                    }

                    setLoading(false);
                } catch (error) {
                    // After an error when fetching log events:

                    // 1. Maybe show an error toast?
                    // TODO: https://app.asana.com/0/1150917131612043/1200693445198179
                    // Validate this with Eric on dev environment
                    if (buildEnvironment !== 'production') {
                        // eslint-disable-next-line no-console
                        console.error(
                            `Encountered an error in LogDataProvider when fetching log events. This message is not shown in production.\nError: ${error}`,
                        );
                    }

                    // 2: Delete the flag for this call site in localStorage for
                    // CheckForScriptBlocker. See: packages/web/src/components/modals/CheckForScriptBlocker/CheckForScriptBlocker.tsx
                    setInLocalStorage<Record<string, number>>(
                        scriptBlockerFlagsLocalStorageKey,
                        (
                            prev: Record<string, number> | null,
                        ): Record<string, number> => {
                            const next = prev === null ? {} : { ...prev };

                            delete next[SCRIPT_BLOCKER_CALL_SITE_ID];

                            return next;
                        },
                    );
                }
            };

            getAndHandleLogEvents();
        }
    }, [handlers, loading, retryQueue, userId]);

    const deleteHandlers = (handlerKey: string): void => {
        setHandlers((prevState: HandlersState): HandlersState => {
            const nextState = { ...prevState };
            const eventTypes = Object.keys(prevState);

            eventTypes.forEach((eventKey: string): void => {
                delete nextState[eventKey][handlerKey];
            });

            return nextState;
        });
    };

    const addHandler = ({
        eventTypes,
        handler,
        handlerKey,
    }: AddHandlerInput): void => {
        setHandlers((prevState: HandlersState): HandlersState => {
            const nextState = { ...prevState };

            eventTypes.forEach((eventType: string) => {
                if (nextState[eventType] === undefined) {
                    nextState[eventType] = {};
                }

                nextState[eventType][handlerKey] = handler;
            });

            return nextState;
        });
    };

    useInterval(fetchLogEvents, pollingInterval);

    return (
        <LogDataContext.Provider
            value={{
                logs,
                addHandler,
                deleteHandlers,
                userId,
            }}
        >
            {children}
        </LogDataContext.Provider>
    );
};
